<!--
  ApprovalQueueRow.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  One row in the Approval Workspace queue. Renders the artwork thumb,
  display name, method/version metadata, current status chip, and the
  stale pill when applicable.

  Props:
    - entry: object -- the flat ApprovalRecord with optional ``artwork``
      enrichment carried through from the Pinia store.
    - selected: boolean -- highlights the row when it is the currently
      open detail item.

  Emits:
    - click -- the page router-pushes /distributor/decorations/approvals
      /<entry.id> on click so deep-linking works (P2-Q3).

  The row deliberately avoids inline action buttons -- the workspace's
  pattern is "row click opens detail; detail panel owns all actions."
-->
<template>
    <button
        type="button"
        class="aw-row"
        :class="{ 'aw-row--selected': selected }"
        :data-testid="`approval-queue-row-${entry.id}`"
        @click="$emit('click')"
    >
        <span class="aw-row__thumb" aria-hidden="true">
            <span class="aw-row__thumb-letter">{{ thumbLetter }}</span>
        </span>
        <span class="aw-row__body">
            <span class="aw-row__title">{{ displayName }}</span>
            <span class="aw-row__meta">{{ metaLine }}</span>
            <span class="aw-row__chips">
                <span
                    class="aw-row__status-chip"
                    :class="`aw-row__status-chip--${entry.status}`"
                    :data-testid="`approval-queue-row-status-${entry.id}`"
                    >{{ statusLabel }}</span
                >
                <span
                    v-if="showStalePill"
                    class="aw-row__stale-pill"
                    :data-testid="`approval-queue-row-stale-${entry.id}`"
                    >{{ stalePillLabel }}</span
                >
            </span>
        </span>
    </button>
</template>

<script>
import { formatArtworkName } from '@/utils/formatArtworkName'
import { STATUS_LABELS, STALE_LABELS } from './filters'

export default {
    name: 'ApprovalQueueRow',
    props: {
        entry: {
            type: Object,
            required: true,
        },
        selected: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click'],
    computed: {
        displayName() {
            return (
                formatArtworkName(
                    this.entry?.artwork?.filename ||
                        this.entry?.artwork?.original_filename,
                ) || `Approval #${this.entry.id}`
            )
        },
        thumbLetter() {
            const s = this.displayName || 'A'
            return s.replace(/[^A-Za-z]/g, '').slice(0, 1).toUpperCase() || 'A'
        },
        statusLabel() {
            return STATUS_LABELS[this.entry?.status] || this.entry?.status || ''
        },
        showStalePill() {
            return (
                this.entry?.status === 'awaiting_approval' &&
                this.entry?.stale_state &&
                this.entry.stale_state !== 'active'
            )
        },
        stalePillLabel() {
            return STALE_LABELS[this.entry?.stale_state] || 'Stale'
        },
        metaLine() {
            const parts = []
            const versionNumber =
                this.entry?.artwork_version?.version_number ||
                this.entry?.artwork?.version
            if (versionNumber != null) {
                parts.push(`v${versionNumber}`)
            }
            const usage = this.entry?.usage_count
            if (typeof usage === 'number' && usage > 0) {
                parts.push(`on ${usage} product${usage === 1 ? '' : 's'}`)
            }
            if (this.entry?.client_name) parts.push(this.entry.client_name)
            return parts.join(' · ')
        },
    },
}
</script>

<style scoped>
.aw-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    border-left: 3px solid transparent;
    background: transparent;
    text-align: left;
    cursor: pointer;
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
}
.aw-row:hover {
    background: var(--tf-surface-alt, #f7fafc);
}
.aw-row--selected {
    background: var(--tf-primary-soft, rgba(25, 118, 210, 0.08));
    border-left-color: var(--tf-primary, #1976d2);
}
.aw-row__thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 6px;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-3, #4a5568);
    font-weight: 600;
}
.aw-row__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
}
.aw-row__title {
    font-size: 13px;
    font-weight: 500;
    color: var(--tf-fg-1, #1a202c);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.aw-row__meta {
    font-size: 11px;
    color: var(--tf-fg-4, #5a6a82);
}
.aw-row__chips {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
}
.aw-row__status-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 10px;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-2, #2d3748);
    font-size: 11px;
    font-weight: 500;
}
.aw-row__status-chip--awaiting_approval {
    background: rgba(25, 118, 210, 0.12);
    color: #0d47a1;
}
.aw-row__status-chip--changes_requested {
    background: #fff3df;
    color: #7a4f00;
}
.aw-row__status-chip--rejected {
    background: rgba(211, 47, 47, 0.12);
    color: #c62828;
}
.aw-row__status-chip--approved {
    background: rgba(56, 142, 60, 0.14);
    color: #2e7d32;
}
.aw-row__stale-pill {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 10px;
    background: rgba(201, 121, 0, 0.14);
    color: #c97900;
    font-size: 11px;
    font-weight: 500;
}
</style>
